import React from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';

import type { SelectFilterOption } from '../../../../../common/constants/filters/select-filter/types';
import { is3pProductKey, ProductKeys } from '../../../../../common/constants/product-keys';
import { useProductConfigs } from '../../../../../common/constants/products';
import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import {
	createOptionLabelFormatter,
	formatAvatarOptionLabel,
} from '../../../../../common/ui/filters/select-filter/option-label';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useFiltersDiscoverability2Enabled,
	usePrimaryProduct,
} from '../../../../../controllers/store/bootstrap';
import { useSelectedProduct } from '../../../../../controllers/store/search';
import { filter as contentFilter } from '../../../../../controllers/store/search/filters/content';
import { filter as spaceFilter } from '../../../../../controllers/store/search/filters/space';

import { i18n } from './messages';

const thirdPartySpaceLabels: Partial<
	Record<
		string,
		{
			label: typeof i18n.drive | typeof i18n.site;
			placeholder: typeof i18n.drivePlaceholder | typeof i18n.sitePlaceholder;
		}
	>
> = {
	[ProductKeys.GoogleDrive]: { label: i18n.drive, placeholder: i18n.drivePlaceholder },
	[ProductKeys.Sharepoint]: { label: i18n.site, placeholder: i18n.sitePlaceholder },
};

const SpaceFilter = ({
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
	onClearOptions,
}: FilterProps): React.JSX.Element | null => {
	const [selectedProduct] = useSelectedProduct();
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [primaryProduct] = usePrimaryProduct();
	const products = useProductConfigs();
	const [
		filterData,
		{
			loadInitialOptions,
			setLookupInput,
			setOption: setOptionForSelectFilter,
			clearLookupInput,
			clearOptions,
		},
	] = useSelectFilter({
		filterId: spaceFilter.id,
	});

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
	} = filterData;

	const setOption = (option: SelectFilterOption | SelectFilterOption[]) => {
		// Validate the content filter for Google Drive, and for SharePoint when its gate is enabled.
		const shouldValidateContentFilter =
			selectedProduct === ProductKeys.GoogleDrive ||
			(selectedProduct === ProductKeys.Sharepoint &&
				fg('rovo_fps_3p_folder_validation_on_space_sharepoint'));
		const validateDependentFilterId = shouldValidateContentFilter ? contentFilter.id : undefined;

		setOptionForSelectFilter(
			option,
			undefined,
			selectedProduct ?? ProductKeys.Confluence,
			validateDependentFilterId,
		);
	};

	const groupedOptions = [
		{
			label: formatMessage(i18n.confluenceFilterLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.confluenceFilterLabelUnselected),
			options: suggestedOptions,
		},
	];

	const thirdPartyLabels = selectedProduct ? thirdPartySpaceLabels[selectedProduct] : undefined;
	const spaceLabel = thirdPartyLabels
		? formatMessage(thirdPartyLabels.label)
		: formatMessage(i18n.space);
	const label = fit === 'compact' && selectedOptions.length > 0 ? '' : spaceLabel;
	const placeholder = thirdPartyLabels
		? formatMessage(thirdPartyLabels.placeholder)
		: formatMessage(i18n.confluenceFilterSpacePlaceholder);

	const isThirdPartySingleSelect = is3pProductKey(selectedProduct ?? '');

	// For 3P single-select products, use the product's own icon instead of the avatar fallback
	// (which shows a person icon when avatarUrl is missing).
	const ProductIcon =
		isThirdPartySingleSelect && selectedProduct
			? products[selectedProduct as ProductKeys]?.Icon
			: undefined;
	const formatOptionLabel = ProductIcon
		? createOptionLabelFormatter(({ option }) => <ProductIcon label={option.label} size="small" />)
		: formatAvatarOptionLabel;

	return (
		<SelectFilter
			contentFit={fit}
			canClearFilter
			checkboxIndicators={!isThirdPartySingleSelect}
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={(option) => {
				setOption(option);
				onFilterChange?.(option);
			}}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={label}
			placeholder={placeholder}
			filterOrControlId={'space'}
			testId="search-dialog-space-filter"
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			formatOptionLabel={formatOptionLabel}
			onClearOptions={() => {
				clearOptions();
				onClearOptions?.({ filterId: spaceFilter.id });
			}}
			iconBefore={
				<FilterProductIcon
					showPrimaryProductIcon={showPrimaryProductIcon}
					primaryProduct={primaryProduct}
				/>
			}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			hideOnHoverOut={hideOnHoverOut}
		/>
	);
};

export default SpaceFilter;
